import cx from 'clsx';
import React, { useContext, useMemo } from 'react';
import { InView } from 'react-intersection-observer';
import {
  Badge, Container, Card, Section, Heading,
  Link,
} from '@printdeal/ui-library';
import { FormattedMessage } from 'react-intl';
import { Image } from '@unpic/react';
import { VIEW_PORT_CHECK_PERCENTAGE } from '../../../gatsby/constants';
import AnalyticsTrackerContext from '../AnalyticsTracker/AnalyticsTrackerContext';
import {
  observerCallback,
  getProductsForPlacements,
  productClickHandlerForDataLayerTrigger,
  getItemsListToDisplay,
} from './recommendations-helper';
import type { RecommendationsProps } from './types';
import { ReviewStars } from '../review-stars/ReviewStars';

const Recommendations = ({
  data,
  config,
  consentData,
  className = '',
  itemsPerRow = 4,
  children = null,
  isPlacementLoaded,
  headingKind = 'h2',
  onView = () => { },
  gridClassName = '',
  anonymousCOAMToken,
  sectionVariant = 'white',
  cardVariant = undefined,
  containerClassName = '',
  clickHandler = () => { },
  overRideCookieConsentForTesting = false,
  inViewThreshold = VIEW_PORT_CHECK_PERCENTAGE,
  showHeader = false,
  eventType,
  pageLayout,
  reviews,
}: RecommendationsProps) => {
  const analyticsTrackerData = useContext(AnalyticsTrackerContext);
  const placementsFromCTRecommendations = data?.placements;
  const productsToDisplayForPlacement = getProductsForPlacements(placementsFromCTRecommendations);

  const itemsToDisplay = useMemo(() =>
    getItemsListToDisplay(productsToDisplayForPlacement, placementsFromCTRecommendations),
  [placementsFromCTRecommendations, productsToDisplayForPlacement]);

  if (!placementsFromCTRecommendations || itemsToDisplay?.length === 0) {
    return null;
  }

  if (!overRideCookieConsentForTesting && !consentData?.statistics) {
    return null;
  }

  if (!isPlacementLoaded) {
    return null;
  }

  if (!productsToDisplayForPlacement || Object.keys(productsToDisplayForPlacement).length === 0) {
    return null;
  }

  // PostCSS will tree-shake interpolated classes, so we have to use a map to circumvent that
  const columnClasses = {
    1: 'lg:pd-grid-cols-1',
    2: 'lg:pd-grid-cols-2',
    3: 'lg:pd-grid-cols-3',
    4: 'lg:pd-grid-cols-4',
    5: 'lg:pd-grid-cols-5',
    6: 'lg:pd-grid-cols-6',
  };

  return (
    <>
      {placementsFromCTRecommendations.map((placement) => (
        <Section
          className={className}
          key={placement.recommendationId}
          variant={sectionVariant}
        >
          {children}
          {productsToDisplayForPlacement[placement.recommendationId]
            && productsToDisplayForPlacement[placement.recommendationId].length > 0
            && (
              <InView
                onChange={
                  (inView, entry) => observerCallback(
                    analyticsTrackerData,
                    inView,
                    entry,
                    anonymousCOAMToken,
                    config,
                    onView,
                    consentData,
                    eventType,
                  )
                }
                threshold={inViewThreshold}
              >
                {({ ref }) => (
                  <div
                    ref={ref}
                    data-recommendations={JSON.stringify({
                      products: productsToDisplayForPlacement[placement.recommendationId],
                      analyticsId: placement.analyticsId,
                      recommendationId: placement.recommendationId,
                      placementId: placement.placementId,
                    })}
                    data-placement-id={placement.placementId}
                  >
                    <Container className={containerClassName}>
                      {showHeader && (
                        <Heading as={headingKind} className="pd-mb-5">{placement.title}</Heading>
                      )}
                      <div className={cx(gridClassName, 'pd-grid pd-gap-6 pd-grid-cols-2', columnClasses[itemsPerRow])}>
                        {itemsToDisplay?.map((item, itemPosition) => (
                          <Card
                            key={item.id}
                            title={item.title}
                            link={(
                              <Link
                                href={item.path}
                                onClick={() => productClickHandlerForDataLayerTrigger(
                                  analyticsTrackerData,
                                  placement.recommendationId,
                                  item.entry,
                                  placement.analyticsId,
                                  placement.placementId,
                                  itemPosition + 1,
                                  anonymousCOAMToken,
                                  config,
                                  clickHandler,
                                  consentData,
                                )}
                              />
                                )}
                            variant={cardVariant}
                            badge={item.discounts?.length ? (
                              <Badge variant="warning">
                                <FormattedMessage
                                  id="product.discount"
                                  defaultMessage="{percentage} % discount"
                                  values={{ percentage: item.discounts[0].discountValue.percentage }}
                                />
                              </Badge>
                            ) : undefined}
                            image={item.image?.url ? (
                              <div className="pd-relative pd-aspect-square">
                                <Image
                                  src={item.image.url}
                                  width={640}
                                  height={640}
                                  alt={item.name || ''}
                                  loading="eager"
                                  className="pd-cover-parent"
                                />
                              </div>
                            ) : undefined}
                          >
                            {pageLayout === 'PPAG' && (
                            <>
                              {reviews
                              && (
                              <div className="pd-flex pd-items-center pd-gap-2">
                                <ReviewStars score={reviews?.average_rating} />
                                <div className="pd-pl-1 pd-text-sm pd-font-semibold">
                                  {reviews.average_rating} ({reviews.results_count})
                                </div>
                              </div>
                              )}
                            </>
                            )}
                          </Card>
                        ))}
                      </div>
                    </Container>
                  </div>
                )}
              </InView>
            )}
        </Section>
      ))}
    </>
  );
};

export default Recommendations;
